import { inject, Injectable } from '@angular/core';
import { SortingParams } from 'src/app/interfaces/connections.interface';
import {
  CommentPosition,
  CommentView,
  FullRootCommentView,
  LoadedCommentsState,
} from 'src/app/interfaces/posts.interface';
import { ListOfCommentReplies, ListOfRootComments, ReplyComment, RootComment } from 'src/app/swagger-api';
import { AscendingCommentSortingStrategyService } from './sorting-strategy/asc-comment-sorting-strategy.service';
import { CommentSortingStrategyService } from './sorting-strategy/comment-sorting-strategy.service';
import { DescendingCommentSortingStrategyService } from './sorting-strategy/desc-comment-sorting-strategy.service';

@Injectable({ providedIn: 'root' })
export class CommentsImporter {
  private rootCommentSortingStrategyService: CommentSortingStrategyService;
  private replyCommentSortingStrategyService: CommentSortingStrategyService;

  private descendingCommentSortingStrategyService = inject(DescendingCommentSortingStrategyService);
  private ascendingCommentSortingStrategyService = inject(AscendingCommentSortingStrategyService);

  public constructor() {
    this.rootCommentSortingStrategyService = this.descendingCommentSortingStrategyService;
    this.replyCommentSortingStrategyService = this.ascendingCommentSortingStrategyService;
  }

  public addNewRootComment(
    loadedComments: LoadedCommentsState,
    newRootComment: FullRootCommentView,
  ): LoadedCommentsState {
    return this.rootCommentSortingStrategyService.addNewRootComment(loadedComments, newRootComment);
  }

  public addNewReply(
    loadedComments: LoadedCommentsState,
    repliedCommentPosition: CommentPosition,
    replyCommentView: CommentView,
  ): LoadedCommentsState {
    return this.replyCommentSortingStrategyService.addNewReply(
      loadedComments,
      repliedCommentPosition,
      replyCommentView,
    );
  }

  public changeCommentsSortingStrategy(sortingStrategy: SortingParams): void {
    this.rootCommentSortingStrategyService =
      sortingStrategy === SortingParams.CreatedAsc
        ? this.ascendingCommentSortingStrategyService
        : this.descendingCommentSortingStrategyService;
  }

  public changeReplySortingStrategy(sortingStrategy: SortingParams): void {
    this.replyCommentSortingStrategyService =
      sortingStrategy === SortingParams.CreatedAsc
        ? this.ascendingCommentSortingStrategyService
        : this.descendingCommentSortingStrategyService;
  }

  public importComments(rootComments: ListOfRootComments): LoadedCommentsState {
    const rootCommentFullViews: FullRootCommentView[] = [];

    rootComments.items.forEach((rootComment: RootComment) => {
      const rootCommentView: CommentView = { source: rootComment, parentCommentId: null };

      const replyCommentViews: CommentView[] = this.importReplyComments(
        rootCommentView,
        rootComment.replies_list.items,
      );

      rootCommentFullViews.push({
        rootCommentView,
        replyCommentViews,
        loadedReplyCommentsCount: replyCommentViews.length,
        totalReplyCommentsCount: rootComment.replies_list.total_count,
      });
    });

    return {
      rootCommentFullViews: this.rootCommentSortingStrategyService.sortRootComments(rootCommentFullViews),
      loadedRootCommentsCount: rootCommentFullViews.length,
      totalRootCommentsCount: rootComments.total_count,
    };
  }

  public mergeLoadedStates(statePrevious: LoadedCommentsState, stateNew: LoadedCommentsState): LoadedCommentsState {
    const newCommentIds: Set<string> = new Set();
    for (let i: number = statePrevious.loadedRootCommentsCount; i < statePrevious.rootCommentFullViews.length; i++) {
      newCommentIds.add(statePrevious.rootCommentFullViews[i].rootCommentView.source.entity_id);
    }

    const newRootCommentViews: FullRootCommentView[] = [...statePrevious.rootCommentFullViews];
    stateNew.rootCommentFullViews.forEach((view: FullRootCommentView) => {
      const commentId: string = view.rootCommentView.source.entity_id;
      if (newCommentIds.has(commentId)) {
        newCommentIds.delete(commentId);
      } else {
        newRootCommentViews.push(view);
      }
    });

    const mergedState: LoadedCommentsState = {
      rootCommentFullViews: newRootCommentViews,
      loadedRootCommentsCount: newRootCommentViews.length - newCommentIds.size,
      totalRootCommentsCount: stateNew.totalRootCommentsCount,
    };

    mergedState.rootCommentFullViews = this.rootCommentSortingStrategyService.sortRootComments(
      mergedState.rootCommentFullViews,
    );

    return mergedState;
  }

  public importReplyComments(parentComment: CommentView, replyComments: ReplyComment[]): CommentView[] {
    const replyCommentViews: CommentView[] = replyComments.map((reply: ReplyComment) => {
      return {
        source: reply,
        parentCommentId: parentComment.source.entity_id,
        repliedUser: parentComment.source.creator,
      };
    });

    return this.replyCommentSortingStrategyService.sortReplies(replyCommentViews);
  }

  public mergeReplyComments(
    parentCommentView: FullRootCommentView,
    extraReplies: ListOfCommentReplies,
  ): FullRootCommentView {
    const newReplyIds: Set<string> = new Set();
    for (
      let i: number = parentCommentView.loadedReplyCommentsCount;
      i < parentCommentView.replyCommentViews.length;
      i++
    ) {
      newReplyIds.add(parentCommentView.replyCommentViews[i].source.entity_id);
    }

    const newReplyCommentViews: CommentView[] = [...parentCommentView.replyCommentViews];

    const extraReplyCommentViews: CommentView[] = this.importReplyComments(
      parentCommentView.rootCommentView,
      extraReplies.items,
    );
    extraReplyCommentViews.forEach((view: CommentView) => {
      const commentId: string = view.source.entity_id;
      if (newReplyIds.has(commentId)) {
        newReplyIds.delete(commentId);
      } else {
        newReplyCommentViews.push(view);
      }
    });

    return {
      rootCommentView: parentCommentView.rootCommentView,
      replyCommentViews: this.replyCommentSortingStrategyService.sortReplies(newReplyCommentViews),
      loadedReplyCommentsCount: newReplyCommentViews.length - newReplyIds.size,
      totalReplyCommentsCount: extraReplies.total_count,
    };
  }
}
